import { Component, Input, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';

type TPdfControlType = 'text' | 'yes/no';

/**
 * This is used when two PDF form fields should
 * map only to one Angular form control. For example,
 * if in the PDF there is a radio button for 'yes'
 * and a radio button for 'no' and those fields are
 * 'Radio Button 1' and 'Radio Button 2' respectively,
 * and they both should map to a single Angular form
 * field called 'primary', then you should have something
 * that looks like this:
 * 
 * ```
 * form: FormGroup<TPdfForm> = new FormGroup<TPdfForm>({
 *   // define 'primary' here
 *   primary: this._fb.group<TPdfControlMulti>({
 *     pdfFields: this._fb.array([
 *       {
 *         // define the 'yes' checkbox here
 *         pdfField: this._fb.control('Radio Button 1'),
 *         value: this._fb.control(true)
 *       },
 *       {
 *         // define the 'no' checkbox here
 *         pdfField: this._fb.control('Radio Button 2'),
 *         value: this._fb.control(false)
 *       }
 *     ])
 *   })
 * })
 * ```
 */
export type TPdfControlMulti = {
  pdfFields: FormArray<FormControl<{
    /**
     * Which field in the PDF does this form field map to?
     */
    pdfField: FormControl<string>,

    /**
     * The value that Angular should use
     */
    value: FormControl<boolean>
  }>>
} & Omit<TPdfControl, 'pdfField'>;

export type TPdfControl = {
  /**
   * Which field in the PDF does this form field map to?
   */
  pdfField: FormControl<string>,

  /**
   * What type of data does this field hold?
   */
  type: FormControl<TPdfControlType>,

  /**
   * The value of the field. This is mutable by the user.
   */
  userValue: FormControl<string | boolean>,

  /**
   * Should this field be read only in the PDF?
   */
  readonly?: FormControl<boolean>
};

export type TPdfForm = {
  [key: string]: FormGroup<TPdfControl> | FormGroup<TPdfControlMulti>
}

enum PdfValue {
  Selected = '0',
  NotSelected = '1'
}

type TPdfFormField = {
  type: TPdfControlType,
  formField: string,
  value?: boolean,
  readonly?: boolean,
};

@Component({
  selector: 'app-pdf-editor',
  templateUrl: './pdf-editor.component.html',
  styleUrl: './pdf-editor.component.scss'
})
export class PdfEditorComponent {
  @Input() pdfSrc: string;
  @Input() form: FormGroup<TPdfForm>
  @Input() showDownloadButtons = false;
  @Input() height: string = '500px';

  /**
   * Useful for debugging or figuring out the name of form fields
   * embedded in the pdf
   */
  @Input() logChanges: boolean = false;

  pdfService = inject(NgxExtendedPdfViewerService);

  private get pdfFormFields(): [string, TPdfFormField][] {
    const options: [string, TPdfFormField][] = [];

    for(const prop of Object.entries(this.form.controls)) {
      if(this.isPdfControl(prop[1])) {
        options.push([
          prop[1].value.pdfField,
          {
            type: prop[1].value.type,
            formField: prop[0],
            // value: prop[1].value.value,
            readonly: !!prop[1].value.readonly
          }
        ]);
      } else {
        for(const field of prop[1].value.pdfFields) {
          options.push([
            field.pdfField.value,
            {
              type: prop[1].value.type,
              formField: prop[0],
              value: field.value.value,
              readonly: !!prop[1].value.readonly
            }
          ]);
        }
      }
    }

    return options;
  }

  private isPdfControl(control: FormGroup<TPdfControl> | FormGroup<TPdfControlMulti>): control is FormGroup<TPdfControl> {
    return (control.value as unknown as TPdfControl).pdfField !== undefined;
  }

  private get pdfFormFieldsObject(): { [key: string]: TPdfFormField } {
    const obj = {};
    const fields = this.pdfFormFields;

    for(const prop of fields) {
      obj[prop[0]] = prop[1];
    }

    return obj;
  }

  private get formValue(): { [key: string]: string | boolean } {
    const obj = {};

    for(const entry of Object.entries(this.form.value)) {
      obj[entry[0]] = entry[1].userValue;
    }

    return obj;
  }

  public async getPdfBlob(): Promise<Blob> {
    return await this.pdfService.getCurrentDocumentAsBlob();
  }

  public async getPdfBase64(): Promise<string | ArrayBuffer> {
    const blob = await this.getPdfBlob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
    
      reader.onload = () => {
        resolve(reader.result);
      };
    });
  }

  private async logPdfBlob(): Promise<void> {
    console.log(await this.getPdfBlob());
  }

  private async logPdfBase64(): Promise<void> {
    console.log(await this.getPdfBase64());
  }

  // this sends data from the angular form to the pdf
  protected get pdfFormData(): { [key: string]: string | number | boolean } {
    const valueMap: typeof this.pdfFormData = {};
    const pdfFields = this.pdfFormFieldsObject;
    const pdfFormFields = this.pdfFormFields;
    const ngFormValue = this.formValue;

    for(const option of pdfFormFields) {
      const value = ngFormValue[option[1].formField];

      switch(option[1].type) {
        case 'yes/no': {
          // get all the fields associated with the control
          const dupes = pdfFormFields.filter(j => j[1].formField === pdfFields[option[0]].formField);

          // radio buttons that have more than one field associated with them
          if(dupes.length > 1) {
            // get the value from the angular form
            const formValue = ngFormValue[dupes[0][1].formField];

            // find the index of the value
            const valueIndex = dupes.findIndex(i => i[1].value === formValue);

            // set form control to 'selected'
            valueMap[dupes[valueIndex][0]] = PdfValue.Selected;

            for(const item of dupes.filter(j => j[0] !== dupes[valueIndex][0])) {
              // set all the others to 'not selected'
              valueMap[item[0]] = PdfValue.NotSelected;
            }
          } else {
            // other radio buttons
            valueMap[option[0]] = ngFormValue[option[1].formField] ? PdfValue.Selected : PdfValue.NotSelected;
          }

          break;
        }
        case 'text':
        default:
          valueMap[option[0]] = value;
          break;
      }
    }

    return valueMap;
  }

  // this sends data from the pdf to the angular form
  protected set pdfFormData(data: { [fieldName: string]: string | number | boolean }) {
    const values = { ...this.formValue } as Required<typeof values>;
    const pdfFields = this.pdfFormFieldsObject;
    const pdfFormFields = this.pdfFormFields;

    for(const prop in data) {
      if(!Object.hasOwn(pdfFields, prop)) {
        continue;
      }

      if(pdfFields[prop].readonly) {
        continue;
      }

      if(pdfFields[prop].type === 'yes/no') {
        // radio buttons

        // get possible options from 'form'
        const options = pdfFormFields.filter(i => i[1].formField === pdfFields[prop].formField);

        // radio buttons that have more than one field associated with them
        if(options.length > 1) {
          // get an array of tuples for each of the possible options
          const pdfValues = Object.entries(data).filter(i => options.map(j => j[0]).includes(i[0]));
          
          // get the option that was selected by the user
          const selectedPdfValue = pdfValues.find(i => i[1] === PdfValue.Selected);

          // this can be undefined if 'form' is not setup correctly
          if(!selectedPdfValue) {
            continue;
          }

          // get the config object for the selected option
          const pdfToFormValue = pdfFields[selectedPdfValue[0]];

          // set the value
          values[pdfFields[prop].formField] = pdfToFormValue.value;
        } else {
          // other radio buttons
          values[pdfFields[prop].formField] = data[prop] === PdfValue.Selected;
        }
      } else {
        // text fields
        values[pdfFields[prop].formField] = data[prop];
      }
    }

    // update the angular form
    for(const value of Object.entries(values)) {
      this.form.controls[value[0]].patchValue({
        userValue: value[1] as string | boolean
      });
    }
  }

  protected formDataChanged(e): void {
    // for debugging purposes and figuring out fields
    // of new PDFs that business wants to support
    if(this.logChanges) {
      console.log(e);
    }

    // detect if there was a change in radio buttons
    // that are split between multiple form controls

    const oldData = this.pdfFormData;
    const newData = e;
    const pdfFormFields = this.pdfFormFields;
    
    const uniqueEntries = pdfFormFields
      .map(i => ({ count: 1, field: i[1].formField })) // map to custom object
      .reduce((a, b) => {
        // count all the occurrences of each field
        a[b.field] = (a[b.field] || 0) + b.count;
        return a;
      }, {});

    // get all form fields that have more than one value associated with them
    const dupes = Object.keys(uniqueEntries).filter(i => uniqueEntries[i] > 1);

    // get map data for each dupe
    const pdfFields = pdfFormFields.filter(i => dupes.includes(i[1].formField as string)).map(i => i[0]);

    // find all the old values
    const oldValues = Object.entries(oldData).filter(i => pdfFields.includes(i[0])).sort();

    // find all the new values
    const newValues = Object.entries(newData).filter(i => pdfFields.includes(i[0])).sort();

    // figure out which value was selected by the user
    const newSelection = newValues.find((val, i) => val[1] !== oldValues[i][1]);

    if(newSelection) {
      // set all related values to 'not selected'.
      // we can't use PdfValue.NotSelected here for all cases. idk why.
      // undefined seems to fix the issue with using PdfValue.NotSelected and
      // works in all cases that i've tried.
      newValues.forEach(i => newData[i[0]] = undefined);

      // set the value changed by the user to the value it was changed to
      newData[newSelection[0]] = newSelection[1];
    }

    // set the form data
    this.pdfFormData = newData;
  }
}
